<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">訂位管理</h1>
      </div>
      <div class="header-actions">
        <router-link
          :to="{ name: 'ClientReservationStoreSettings' }"
          class="btn btn-secondary"
        >
          <i class="ri-settings-3-line"></i>
          訂位參數設定
        </router-link>
        <router-link :to="{ name: 'ClientReservationCreate' }" class="btn btn-primary">
          <i class="ri-add-line"></i>
          新增訂位
        </router-link>
      </div>
    </div>

    <div class="page-container">
    <section class="store-filter">
      <div class="section-heading">
        <div>
          <h2>選擇門市</h2>
          <p>一次顯示一間門市，可切換行事曆或訂位列表</p>
        </div>
        <span v-if="selectedStore" class="reservation-count">
          {{ reservationCountLabel }}
        </span>
      </div>
      <StoreSelector
        v-model="selectedStoreId"
        :stores="stores"
        :loading="storesLoading"
        :clearable="false"
        @change="handleStoreChange"
      />
    </section>

    <section class="calendar-card">
      <div v-if="!storesLoading && stores.length === 0" class="empty-state">
        <i class="ri-store-2-line"></i>
        <strong>目前沒有開放訂位的門市</strong>
        <span>請先到訂位參數設定開啟門市預約，再查看總覽。</span>
        <router-link
          :to="{ name: 'ClientReservationStoreSettings' }"
          class="btn btn-sm btn-primary"
        >
          前往設定
        </router-link>
      </div>

      <div v-else-if="selectedStoreId" class="calendar-wrapper">
        <div class="display-mode-toolbar">
          <div class="display-mode-switch" role="group" aria-label="訂位顯示模式">
            <button
              type="button"
              :class="{ active: displayMode === 'calendar' }"
              @click="setDisplayMode('calendar')"
            >
              <i class="ri-calendar-line"></i>行事曆
            </button>
            <button
              type="button"
              :class="{ active: displayMode === 'list' }"
              @click="setDisplayMode('list')"
            >
              <i class="ri-list-check-2"></i>訂位列表
            </button>
          </div>
          <div v-if="displayMode === 'list'" class="list-filters">
            <input
              v-model="searchQuery"
              type="text"
              class="form-control list-search"
              placeholder="搜尋編號、姓名、電話..."
              @input="handleSearch"
            />
            <select v-model="filterStatus" class="form-control" @change="loadReservationList">
              <option value="">全部狀態</option>
              <option value="confirmed">已確認</option>
              <option value="seated">已入座</option>
              <option value="completed">已用餐完畢</option>
              <option value="cancelled">已取消</option>
              <option value="no_show">未出席</option>
            </select>
            <label class="overdue-toggle">
              <input v-model="showOverdue" type="checkbox" />
              <span>顯示逾時{{ staleReservationCount ? `（${staleReservationCount}）` : '' }}</span>
            </label>
          </div>
        </div>

        <div v-if="displayMode === 'calendar' && showMissingHoursWarning" class="hours-warning">
          <i class="ri-error-warning-line"></i>
          <div>
            <strong>此門市尚未設定可營業時段</strong>
            <span>目前視為全日不營業，無法提供訂位時段。</span>
          </div>
          <router-link
            :to="{ name: 'ClientReservationStoreSettings' }"
            class="btn btn-sm btn-secondary"
          >
            前往設定
          </router-link>
        </div>

        <div v-show="displayMode === 'calendar'">
          <div class="calendar-legend">
            <span><i class="legend-swatch business"></i>營業時段</span>
            <span><i class="legend-swatch non-business"></i>非營業／固定公休</span>
            <span><i class="legend-swatch closure"></i>特殊店休</span>
          </div>
          <div v-if="calendarLoading" class="calendar-loading">
            <i class="ri-loader-4-line spin"></i>
            載入門市行事曆中...
          </div>
          <FullCalendar ref="calendar" :options="calendarOptions" />
        </div>

        <div v-if="displayMode === 'list'" class="reservation-list-view">
          <div v-if="listLoading" class="list-loading">
            <i class="ri-loader-4-line spin"></i>載入訂位列表中...
          </div>
          <div v-else-if="!visibleReservationList.length" class="empty-state compact">
            <i class="ri-calendar-check-line"></i>
            <strong>{{ emptyListText }}</strong>
          </div>
          <div v-else class="table-wrapper">
            <table class="reservation-table">
              <thead>
                <tr>
                  <th>訂位時間</th>
                  <th>顧客</th>
                  <th>電話</th>
                  <th>人數</th>
                  <th>座位</th>
                  <th>狀態</th>
                  <th>訂位編號</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in visibleReservationList" :key="item.id">
                  <td>
                    <strong>{{ displayReservationDate(item) }}</strong>
                  </td>
                  <td>{{ item.contactName || '-' }}</td>
                  <td>{{ item.contactPhone || '-' }}</td>
                  <td>{{ item.guestCount || 0 }}人</td>
                  <td>
                    <span v-if="item.isUpwardTableAssignment" class="upward-badge">向上相容</span>
                    <span v-else>-</span>
                  </td>
                  <td>
                    <span
                      class="status-badge"
                      :style="{ '--status-color': statusStyle(item.status).color }"
                    >
                      {{ statusStyle(item.status).label }}
                    </span>
                  </td>
                  <td>{{ item.reservationNumber || '-' }}</td>
                  <td>
                    <div class="action-buttons">
                      <button
                        v-if="item.status === 'confirmed'"
                        type="button"
                        class="btn btn-sm btn-info"
                        @click="handleSeat(item)"
                      >
                        已入座
                      </button>
                      <button
                        v-if="item.status === 'confirmed' || item.status === 'seated'"
                        type="button"
                        class="btn btn-sm btn-success"
                        @click="handleCompleteDining(item)"
                      >
                        已用餐完畢
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm btn-secondary"
                        @click="openReservationItem(item)"
                      >
                        查看
                      </button>
                      <button
                        v-if="item.status !== 'cancelled'"
                        type="button"
                        class="btn btn-sm btn-secondary"
                        @click="handleEdit(item)"
                      >
                        編輯
                      </button>
                      <button
                        v-if="item.status !== 'cancelled'"
                        type="button"
                        class="btn btn-sm btn-warning"
                        @click="handleCancel(item)"
                      >
                        取消
                      </button>
                      <button
                        v-if="item.status === 'cancelled'"
                        type="button"
                        class="btn btn-sm btn-danger"
                        @click="handleDelete(item)"
                      >
                        刪除
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
    </div>
  </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue3';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import zhTwLocale from '@fullcalendar/core/locales/zh-tw';
import StoreSelector from '@/components/StoreSelector.vue';
import { reservationService } from '@/api/modules/reservations.js';
import { storeService } from '@/api/modules/stores.js';
import { showConfirm, showDeleteConfirm, showError, showSuccess } from '@/utils/swal.js';

const statusStyles = {
  confirmed: { label: '已確認', color: '#2563eb' },
  seated: { label: '已入座', color: '#7c3aed' },
  completed: { label: '已用餐完畢', color: '#059669' },
  cancelled: { label: '已取消', color: '#94a3b8' },
  no_show: { label: '未出席', color: '#dc2626' }
};

function localDateKey(value) {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value) && !value.includes('T')) {
    return value.slice(0, 10);
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function addDateDays(value, days) {
  const date = new Date(`${value}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function addMinutesToTime(timeValue, minutesToAdd) {
  return shiftDateTime('2000-01-01', timeValue, minutesToAdd).time;
}

function shiftDateTime(date, timeValue, minutesToAdd) {
  const [hours, minutes] = String(timeValue || '00:00').slice(0, 5).split(':').map(Number);
  const total = (hours * 60) + minutes + Number(minutesToAdd || 0);
  const dayOffset = Math.floor(total / 1440);
  const remainder = ((total % 1440) + 1440) % 1440;
  const nextHours = Math.floor(remainder / 60);
  const nextMinutes = remainder % 60;
  return {
    date: dayOffset ? addDateDays(date, dayOffset) : date,
    time: `${String(nextHours).padStart(2, '0')}:${String(nextMinutes).padStart(2, '0')}`
  };
}

export default {
  name: 'ClientReservations',
  components: { FullCalendar, StoreSelector },
  data() {
    return {
      stores: [],
      storesLoading: false,
      hoursLoading: false,
      reservationsLoading: false,
      hasConfiguredHours: null,
      selectedStoreId: '',
      visibleRange: null,
      events: [],
      requestSequence: 0,
      displayMode: 'calendar',
      reservationList: [],
      listLoading: false,
      listRequestSequence: 0,
      searchQuery: '',
      filterStatus: '',
      showOverdue: false,
      searchTimer: null,
      calendarOptions: {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
        locale: zhTwLocale,
        initialView: 'timeGridWeek',
        firstDay: 1,
        height: 'auto',
        nowIndicator: true,
        allDaySlot: true,
        slotMinTime: '08:00:00',
        slotMaxTime: '22:00:00',
        slotDuration: '00:30:00',
        scrollTime: '11:00:00',
        dayMaxEvents: true,
        eventDisplay: 'block',
        displayEventEnd: false,
        eventContent: (info) => this.renderCalendarEventContent(info),
        businessHours: [],
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: 'timeGridDay,timeGridWeek,dayGridMonth'
        },
        buttonText: {
          today: '今天',
          day: '當日',
          week: '整週',
          month: '整月'
        },
        events: (info, successCallback, failureCallback) =>
          this.fetchCalendarEvents(info, successCallback, failureCallback),
        datesSet: (info) => this.handleDatesSet(info),
        eventClick: (info) => this.openReservation(info),
        eventDidMount: (info) => {
          const closure = info.event.extendedProps.closure;
          if (closure) {
            info.el.title = `特殊店休｜${closure.name}`;
            return;
          }
          const item = info.event.extendedProps.reservation;
          if (!item) return;
          const status = statusStyles[item.status]?.label || item.status;
          info.el.title = [
            this.reservationTimeLabel(item),
            item.contactName || '未填姓名',
            `${item.guestCount || 0}人`,
            item.isUpwardTableAssignment ? '向上相容' : null,
            status
          ].filter(Boolean).join('｜');
        }
      }
    };
  },
  computed: {
    selectedStore() {
      return this.stores.find((store) => store.id === this.selectedStoreId) || null;
    },
    visibleRangeLabel() {
      if (!this.visibleRange) return '';
      const labels = {
        timeGridDay: '當日',
        timeGridWeek: '本週',
        dayGridMonth: '本月'
      };
      return labels[this.visibleRange.viewType] || '目前區間';
    },
    reservationCountLabel() {
      if (this.displayMode === 'list') {
        const revealingOverdue = this.showOverdue || this.searchQuery || this.isTerminalStatusFilter;
        const hidden = revealingOverdue ? 0 : this.staleReservationCount;
        const visible = this.visibleReservationList.length;
        return hidden ? `共 ${visible} 筆，另有 ${hidden} 筆逾時` : `共 ${visible} 筆`;
      }
      return `${this.visibleRangeLabel}共 ${this.events.length} 筆`;
    },
    staleReservationCount() {
      return this.reservationList.filter((item) => this.isStaleReservation(item)).length;
    },
    visibleReservationList() {
      if (this.showOverdue || this.searchQuery || this.isTerminalStatusFilter) {
        return this.reservationList;
      }
      return this.reservationList.filter((item) => !this.isStaleReservation(item));
    },
    isTerminalStatusFilter() {
      return ['completed', 'cancelled', 'no_show'].includes(this.filterStatus);
    },
    emptyListText() {
      if (!this.reservationList.length) return '目前沒有訂位資料';
      return '目前沒有進行中的訂位。可開啟「顯示逾時」查看已過期的預約。';
    },
    calendarLoading() {
      return this.hoursLoading || this.reservationsLoading;
    },
    showMissingHoursWarning() {
      return this.hasConfiguredHours === false;
    },
    slotDurationMinutes() {
      const value = Number(this.selectedStore?.slotDuration);
      return value > 0 ? value : 60;
    },
    diningDurationMinutes() {
      if (!this.selectedStore?.limitDiningDuration) return 0;
      const minutes = Number(this.selectedStore.diningDurationMinutes);
      return minutes > 0 ? minutes : 0;
    },
    eventBlockMinutes() {
      return this.diningDurationMinutes || this.slotDurationMinutes;
    }
  },
  async mounted() {
    await this.loadStores();
  },
  beforeUnmount() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },
  methods: {
    async loadStores() {
      this.storesLoading = true;
      try {
        const response = await storeService.getStores({
          page: 1,
          pageSize: 100,
          bookingEnabled: true,
          sortBy: 'storeName',
          order: 'asc'
        });
        this.stores = (response?.data?.items || []).filter((store) => store.bookingEnabled);
        const queryStoreId = typeof this.$route.query.storeId === 'string'
          ? this.$route.query.storeId
          : '';
        const queryExists = queryStoreId && this.stores.some((store) => store.id === queryStoreId);
        if (!this.selectedStoreId && this.stores.length) {
          this.selectedStoreId = queryExists ? queryStoreId : this.stores[0].id;
          await this.$nextTick();
          await this.handleStoreChange();
        }
      } catch (error) {
        await this.handleError(error, '門市載入失敗');
      } finally {
        this.storesLoading = false;
      }
    },
    handleDatesSet(info) {
      const inclusiveEnd = new Date(info.end);
      inclusiveEnd.setDate(inclusiveEnd.getDate() - 1);
      this.visibleRange = {
        startDate: localDateKey(info.start),
        endDate: localDateKey(inclusiveEnd),
        viewType: info.view.type
      };
    },
    refreshCalendar() {
      this.$refs.calendar?.getApi().refetchEvents();
    },
    async handleStoreChange() {
      if (this.selectedStoreId) {
        const nextQuery = { ...this.$route.query, storeId: this.selectedStoreId };
        this.$router.replace({ query: nextQuery }).catch(() => {});
      }
      const showDiningRange = this.diningDurationMinutes > 0;
      this.calendarOptions.displayEventEnd = showDiningRange;
      this.$refs.calendar?.getApi()?.setOption('displayEventEnd', showDiningRange);
      await this.loadStoreHours();
      if (this.displayMode === 'list') {
        await this.loadReservationList();
      } else {
        this.refreshCalendar();
      }
    },
    async setDisplayMode(mode) {
      if (this.displayMode === mode) return;
      this.displayMode = mode;
      if (mode === 'list') {
        await this.loadReservationList();
      } else {
        await this.$nextTick();
        this.$refs.calendar?.getApi().updateSize();
        this.refreshCalendar();
      }
    },
    handleSearch() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.loadReservationList();
      }, 300);
    },
    async loadReservationList() {
      if (!this.selectedStoreId) {
        this.reservationList = [];
        return;
      }
      const sequence = ++this.listRequestSequence;
      const storeId = this.selectedStoreId;
      this.listLoading = true;
      try {
        const params = {
          page: 1,
          pageSize: 100,
          storeId,
          search: this.searchQuery || undefined,
          status: this.filterStatus || undefined,
          sortBy: 'reservationDate',
          order: 'asc'
        };
        const firstResponse = await reservationService.getReservations(params);
        const firstData = firstResponse?.data || {};
        const items = [...(firstData.items || [])];
        const totalPages = firstData.pagination?.totalPages || 1;
        if (totalPages > 1) {
          const remaining = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, index) =>
              reservationService.getReservations({ ...params, page: index + 2 })
            )
          );
          remaining.forEach((response) => items.push(...(response?.data?.items || [])));
        }
        if (sequence !== this.listRequestSequence || storeId !== this.selectedStoreId) return;
        this.reservationList = items.sort(
          (left, right) => this.reservationTimestamp(left) - this.reservationTimestamp(right)
        );
      } catch (error) {
        if (sequence === this.listRequestSequence) {
          this.reservationList = [];
          await this.handleError(error, '訂位列表載入失敗');
        }
      } finally {
        if (sequence === this.listRequestSequence) this.listLoading = false;
      }
    },
    async loadStoreHours() {
      if (!this.selectedStoreId) {
        this.hasConfiguredHours = null;
        this.calendarOptions.businessHours = [];
        return;
      }
      const storeId = this.selectedStoreId;
      this.hasConfiguredHours = null;
      this.hoursLoading = true;
      try {
        const response = await storeService.getStoreHours(storeId);
        if (storeId !== this.selectedStoreId) return;
        const hours = response?.data?.hours || [];
        const businessHours = hours.flatMap((day) => {
          if (!day.isOpen) return [];
          let slots = Array.isArray(day.timeSlots)
            ? day.timeSlots.filter((slot) => slot?.openTime && slot?.closeTime)
            : [];
          if (!slots.length && day.openTime && day.closeTime) {
            slots = [{ openTime: day.openTime, closeTime: day.closeTime }];
          }
          return slots.map((slot) => ({
            daysOfWeek: [Number(day.dayOfWeek)],
            startTime: this.shortTime(slot.openTime),
            endTime: this.shortTime(slot.closeTime)
          }));
        });
        this.hasConfiguredHours = businessHours.length > 0;
        this.calendarOptions.businessHours = businessHours.length
          ? businessHours
          : [{
            daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
            startTime: '00:00:00',
            endTime: '00:00:01'
          }];
      } catch (error) {
        if (storeId === this.selectedStoreId) {
          this.hasConfiguredHours = null;
          this.calendarOptions.businessHours = [];
          await this.handleError(error, '門市營業時間載入失敗');
        }
      } finally {
        if (storeId === this.selectedStoreId) this.hoursLoading = false;
      }
    },
    async fetchCalendarEvents(info, successCallback, failureCallback) {
      if (!this.selectedStoreId) {
        this.events = [];
        successCallback([]);
        return;
      }

      const sequence = ++this.requestSequence;
      this.reservationsLoading = true;
      try {
        const inclusiveEnd = new Date(info.end);
        inclusiveEnd.setDate(inclusiveEnd.getDate() - 1);
        const startDate = localDateKey(info.start);
        const endDate = localDateKey(inclusiveEnd);
        const params = {
          page: 1,
          pageSize: 100,
          storeId: this.selectedStoreId,
          startDate,
          endDate
        };
        const [firstResponse, closures] = await Promise.all([
          reservationService.getReservations(params),
          this.loadCalendarClosures(startDate, endDate)
        ]);
        const firstData = firstResponse?.data || {};
        const items = [...(firstData.items || [])];
        const totalPages = firstData.pagination?.totalPages || 1;

        if (totalPages > 1) {
          const remaining = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, index) =>
              reservationService.getReservations({ ...params, page: index + 2 })
            )
          );
          remaining.forEach((response) => {
            items.push(...(response?.data?.items || []));
          });
        }

        if (sequence !== this.requestSequence) {
          successCallback([]);
          return;
        }
        this.events = items.map((item) => this.toCalendarEvent(item));
        const closureEvents = this.toClosureBackgroundEvents(closures, info.start, info.end);
        successCallback([...this.events, ...closureEvents]);
      } catch (error) {
        if (sequence === this.requestSequence) {
          this.events = [];
          failureCallback(error);
          await this.handleError(error, '訂位載入失敗');
        }
      } finally {
        if (sequence === this.requestSequence) this.reservationsLoading = false;
      }
    },
    renderCalendarEventContent(info) {
      const range = info.event.extendedProps.timeRange;
      if (!range) return undefined;
      const [startLabel, endLabel] = String(range).split('–');
      const root = document.createElement('div');
      root.className = 'reservation-event-body';
      const time = document.createElement('div');
      time.className = 'reservation-event-range';
      const start = document.createElement('span');
      start.textContent = startLabel;
      const end = document.createElement('span');
      end.textContent = endLabel;
      time.append(start, end);
      const title = document.createElement('div');
      title.className = 'fc-event-title';
      title.textContent = info.event.title;
      root.append(time, title);
      return { domNodes: [root] };
    },
    reservationTimeLabel(item) {
      const startTime = this.shortTime(item.reservationTime);
      if (!this.diningDurationMinutes) return startTime;
      const date = localDateKey(item.reservationDate);
      const end = shiftDateTime(date, startTime, this.diningDurationMinutes);
      const endLabel = end.date === date ? end.time : `翌日 ${end.time}`;
      return `${startTime}–${endLabel}`;
    },
    toCalendarEvent(item) {
      const date = localDateKey(item.reservationDate);
      const startTime = this.shortTime(item.reservationTime);
      const end = shiftDateTime(date, startTime, this.eventBlockMinutes);
      const status = statusStyles[item.status] || { label: item.status, color: '#64748b' };
      const customer = item.contactName || item.contactPhone || '未填姓名';
      return {
        id: item.id,
        title: `${customer}・${item.guestCount || 0}人${item.isUpwardTableAssignment ? '・向上' : ''}`,
        start: `${date}T${startTime}`,
        end: `${end.date}T${end.time}`,
        backgroundColor: status.color,
        borderColor: status.color,
        classNames: [
          ...(item.status === 'cancelled' ? ['is-cancelled'] : []),
          ...(item.isUpwardTableAssignment ? ['is-upward'] : [])
        ],
        extendedProps: {
          reservation: item,
          statusLabel: status.label,
          timeRange: this.diningDurationMinutes ? this.reservationTimeLabel(item) : ''
        }
      };
    },
    openReservation(info) {
      if (info.event.extendedProps.closure) return;
      this.$router.push({
        name: 'ClientReservationDetail',
        params: { id: info.event.id }
      });
    },
    async loadCalendarClosures(startDate, endDate) {
      const response = await reservationService.getStoreClosuresForCalendar({
        storeId: this.selectedStoreId,
        startDate,
        endDate
      });
      return (response?.data?.items || []).filter((item) => item.isAllDay);
    },
    toClosureBackgroundEvents(closures, rangeStart, rangeEnd) {
      const events = [];
      const startDate = localDateKey(rangeStart);
      const endDate = localDateKey(rangeEnd);
      for (const closure of closures) {
        for (let date = startDate; date < endDate; date = addDateDays(date, 1)) {
          const closureStart = localDateKey(closure.startDate);
          const closureEnd = localDateKey(closure.endDate) || closureStart;
          if (date < closureStart || date > closureEnd) continue;
          events.push({
            id: `closure-${closure.id}-${date}`,
            title: closure.name,
            start: date,
            end: addDateDays(date, 1),
            allDay: true,
            display: 'background',
            backgroundColor: '#fecaca',
            classNames: ['reservation-closure-background'],
            extendedProps: { closure }
          });
        }
      }
      return events;
    },
    openReservationItem(item) {
      this.$router.push({
        name: 'ClientReservationDetail',
        params: { id: item.id }
      });
    },
    handleEdit(item) {
      this.$router.push({
        name: 'ClientReservationEdit',
        params: { id: item.id }
      });
    },
    async handleCancel(item) {
      const confirmed = await showDeleteConfirm(
        '確定要取消此訂位嗎？',
        '取消後訂位狀態會變更為「已取消」，但記錄仍會保留。',
        'warning',
        '確定取消'
      );
      if (!confirmed) return;
      try {
        await reservationService.cancelReservation(item.id);
        showSuccess('訂位已取消');
        await this.reloadCurrentView();
      } catch (error) {
        await this.handleError(error, '取消訂位失敗');
      }
    },
    async handleSeat(item) {
      const confirmed = await showConfirm(
        '設定為已入座？',
        '客人已到店入座。已入座的訂位不會被逾時未到自動取消。',
        '已入座',
        '取消',
        'question'
      );
      if (!confirmed) return;
      try {
        await reservationService.updateReservationStatus(item.id, 'seated');
        showSuccess('已入座');
        await this.reloadCurrentView();
      } catch (error) {
        await this.handleError(error, '設定已入座失敗');
      }
    },
    async handleCompleteDining(item) {
      const confirmed = await showConfirm(
        '設定為已用餐完畢？',
        '座位會空出來，並通知下一位人數符合的候位顧客。',
        '已用餐完畢',
        '取消',
        'question'
      );
      if (!confirmed) return;
      try {
        const response = await reservationService.updateReservationStatus(item.id, 'completed');
        showSuccess('已用餐完畢', this.waitlistNoticeText(response?.data?.waitlistNotification));
        await this.reloadCurrentView();
      } catch (error) {
        await this.handleError(error, '設定用餐完畢失敗');
      }
    },
    waitlistNoticeText(notice) {
      if (!notice || notice.reason === 'none') return '目前沒有等待中的候位。';
      if (notice.notified) {
        const channel = notice.channel === 'line' ? 'LINE' : '簡訊';
        const queue = notice.queueNumber ? `（${notice.queueNumber} 號）` : '';
        return `已用${channel}通知候位顧客 ${notice.guestName || ''}${queue}。`;
      }
      if (notice.reason === 'no_match') return '目前沒有人數符合這張桌的候位顧客。';
      if (notice.reason === 'disabled') {
        return '尚未開啟候位通知。請到訂位參數設定開啟 LINE 或簡訊。';
      }
      return notice.error ? `候位通知未送出：${notice.error}` : '候位通知未送出。';
    },
    async handleDelete(item) {
      const confirmed = await showDeleteConfirm(
        '確定要永久刪除此訂位記錄嗎？',
        '此操作無法復原！',
        'error',
        '確定刪除'
      );
      if (!confirmed) return;
      try {
        await reservationService.deleteReservation(item.id);
        showSuccess('訂位記錄已刪除');
        await this.reloadCurrentView();
      } catch (error) {
        await this.handleError(error, '刪除訂位失敗');
      }
    },
    async reloadCurrentView() {
      if (this.displayMode === 'list') {
        await this.loadReservationList();
      } else {
        this.refreshCalendar();
      }
    },
    reservationTimestamp(item) {
      const date = localDateKey(item.reservationDate);
      return new Date(`${date}T${this.shortTime(item.reservationTime)}:00+08:00`).getTime();
    },
    isStaleReservation(item) {
      if (!item) return false;
      // 已結束的訂位不再佔用進行中列表；預約時間已過的也一樣，已入座或已用餐完畢都不例外
      if (['completed', 'cancelled', 'no_show'].includes(item.status)) return true;
      const timestamp = this.reservationTimestamp(item);
      return Number.isFinite(timestamp) && timestamp < Date.now();
    },
    displayReservationDate(item) {
      const date = localDateKey(item.reservationDate);
      const label = new Intl.DateTimeFormat('zh-TW', {
        month: '2-digit',
        day: '2-digit',
        weekday: 'short',
        timeZone: 'Asia/Taipei'
      }).format(new Date(`${date}T00:00:00+08:00`));
      return `${label} ${this.shortTime(item.reservationTime)}`;
    },
    statusStyle(status) {
      return statusStyles[status] || { label: status, color: '#64748b' };
    },
    shortTime(value) {
      return String(value || '').slice(0, 5);
    },
    async handleError(error, fallback) {
      const message = error?.response?.data?.message || error?.message || fallback;
      await showError('操作失敗', message);
    }
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.store-filter,
.calendar-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  margin-bottom: 20px;
  padding: 20px;
}

.section-heading {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 14px;
}

.section-heading h2 {
  color: #1e293b;
  font-size: 18px;
  margin: 0;
}

.section-heading p {
  color: #64748b;
  font-size: 13px;
  margin: 4px 0 0;
}

.reservation-count {
  background: #eef2ff;
  border-radius: 999px;
  color: #4338ca;
  font-size: 13px;
  font-weight: 600;
  padding: 7px 12px;
  white-space: nowrap;
}

.calendar-wrapper {
  min-height: 560px;
  position: relative;
}

.display-mode-toolbar {
  align-items: center;
  display: flex;
  gap: 14px;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.display-mode-switch {
  background: #f1f5f9;
  border-radius: 9px;
  display: inline-flex;
  padding: 4px;
}

.display-mode-switch button {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 7px;
  color: #64748b;
  cursor: pointer;
  display: inline-flex;
  font-weight: 600;
  gap: 6px;
  padding: 8px 14px;
}

.display-mode-switch button.active {
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.14);
  color: #4338ca;
}

.list-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.list-search {
  min-width: 220px;
}

.overdue-toggle {
  align-items: center;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  color: #334155;
  cursor: pointer;
  display: inline-flex;
  font-weight: 600;
  gap: 8px;
  min-height: 38px;
  padding: 0 12px;
  white-space: nowrap;
}

.overdue-toggle input {
  accent-color: #4338ca;
  height: 16px;
  width: 16px;
}

.reservation-list-view {
  position: relative;
}

.list-loading {
  align-items: center;
  color: #475569;
  display: flex;
  font-weight: 600;
  gap: 8px;
  justify-content: center;
  min-height: 260px;
}

.reservation-table {
  min-width: 1080px;
}

.reservation-table td,
.reservation-table th {
  vertical-align: middle;
}

.status-badge {
  background: color-mix(in srgb, var(--status-color) 12%, white);
  border: 1px solid color-mix(in srgb, var(--status-color) 35%, white);
  border-radius: 999px;
  color: var(--status-color);
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 9px;
  white-space: nowrap;
}

.upward-badge {
  background: #fff7ed;
  border: 1px solid #fdba74;
  border-radius: 999px;
  color: #c2410c;
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 9px;
  white-space: nowrap;
}

:deep(.fc-event.is-upward) {
  box-shadow: inset 0 0 0 1px rgba(194, 65, 12, 0.35);
}

.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.hours-warning {
  align-items: center;
  background: #fff7ed;
  border: 1px solid #fdba74;
  border-radius: 10px;
  color: #9a3412;
  display: flex;
  gap: 12px;
  margin-bottom: 14px;
  padding: 12px 14px;
}

.hours-warning > i {
  color: #ea580c;
  flex-shrink: 0;
  font-size: 24px;
}

.hours-warning > div {
  display: grid;
  flex: 1;
  gap: 2px;
}

.hours-warning span {
  font-size: 13px;
}

.calendar-legend {
  align-items: center;
  color: #64748b;
  display: flex;
  flex-wrap: wrap;
  font-size: 12px;
  gap: 16px;
  justify-content: flex-end;
  margin-bottom: 12px;
}

.calendar-legend span {
  align-items: center;
  display: inline-flex;
  gap: 6px;
}

.legend-swatch {
  border: 1px solid #cbd5e1;
  border-radius: 3px;
  display: inline-block;
  height: 12px;
  width: 18px;
}

.legend-swatch.business {
  background: #fff;
}

.legend-swatch.non-business {
  background: repeating-linear-gradient(
    -45deg,
    #e2e8f0,
    #e2e8f0 3px,
    #f8fafc 3px,
    #f8fafc 6px
  );
}

.legend-swatch.closure {
  background: #fecaca;
  border-color: #f87171;
}

.calendar-loading {
  align-items: center;
  background: rgba(255, 255, 255, 0.82);
  color: #475569;
  display: flex;
  font-weight: 600;
  gap: 8px;
  inset: 0;
  justify-content: center;
  position: absolute;
  z-index: 5;
}

.empty-state {
  align-items: center;
  color: #64748b;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  min-height: 280px;
  text-align: center;
}

.empty-state i {
  color: #94a3b8;
  font-size: 42px;
}

.empty-state strong {
  color: #334155;
  font-size: 17px;
}

.empty-state.compact {
  min-height: 260px;
}

.spin {
  animation: spin 0.8s linear infinite;
  display: inline-block;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

:deep(.fc .fc-button-primary) {
  background-color: #fff;
  border-color: #cbd5e1;
  color: #334155;
}

:deep(.fc .fc-button-primary:hover),
:deep(.fc .fc-button-primary:not(:disabled).fc-button-active) {
  background-color: #4f46e5;
  border-color: #4f46e5;
  color: #fff;
}

:deep(.fc .fc-toolbar-title) {
  color: #1e293b;
  font-size: 20px;
}

:deep(.fc .fc-col-header-cell.fc-day-sat) {
  background-color: #dcfce7;
}

:deep(.fc .fc-col-header-cell.fc-day-sun) {
  background-color: #fee2e2;
}

:deep(.fc .fc-col-header-cell.fc-day-sat .fc-col-header-cell-cushion),
:deep(.fc .fc-col-header-cell.fc-day-sun .fc-col-header-cell-cushion) {
  color: #111827;
}

:deep(.fc .fc-event) {
  cursor: pointer;
  padding: 2px 4px;
}

:deep(.reservation-event-range) {
  display: flex;
  flex-wrap: wrap;
  column-gap: 2px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
}

:deep(.reservation-event-range span:first-child::after) {
  content: '–';
}

:deep(.fc .fc-event.is-cancelled) {
  opacity: 0.62;
  text-decoration: line-through;
}

:deep(.fc .fc-non-business) {
  background: repeating-linear-gradient(
    -45deg,
    rgba(203, 213, 225, 0.58),
    rgba(203, 213, 225, 0.58) 4px,
    rgba(241, 245, 249, 0.72) 4px,
    rgba(241, 245, 249, 0.72) 8px
  );
  opacity: 1;
}

:deep(.fc .reservation-closure-background) {
  background-color: #fecaca !important;
  opacity: 0.78;
}

@media (max-width: 767px) {
  .section-heading,
  .display-mode-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .reservation-count {
    align-self: flex-start;
  }

  .list-search {
    min-width: 0;
    width: 100%;
  }
}
</style>
